import { themeAtom } from "../../shared/atoms";
import { Bq5CargoArrangementsM } from "../models/bq5-cargo-arrangements.model";
import { InputCell } from "../services/baltic-forms.utils";
import { useAtomValue } from "jotai";
import React from "react";
import { twMerge } from "tailwind-merge";

export function Bq5Holds(
  form: Bq5CargoArrangementsM,
  handleChange: (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => void,
  handleHoldDimensionChange: (index: number, field: string, value: string) => void,
  handleHoldCapacityChange: (index: number, field: "grainCapacity" | "baleCapacity", value: string) => void,
  handleTanktopsStrengthsChange: (index: number, value: string) => void,
  handleFloorMeasurementChange: (index: number, value: string) => void,
  toggleComments: (inputId: string) => void,
  isCommentsVisible: (inputId: string) => boolean,
  readonly: boolean,
) {
  const theme = useAtomValue(themeAtom);

  return (
    <div>
      <h2 className="mb-2 mt-[120px] text-lg font-semibold">Holds</h2>

      <div className="flex flex-row gap-5">
        {/* Number of holds */}
        <div className="">
          <label className="block text-sm font-bold">Number</label>
          <input
            disabled={readonly}
            type="number"
            name="numberOfHolds"
            value={form.numberOfHolds}
            onChange={handleChange}
            className={twMerge("mt-1 w-[100px] rounded-md border p-2 focus:outline-none focus:ring")}
          />
        </div>

        {/* Holds dimensions */}
        <div className="w-full">
          <label className="block text-sm font-bold">Holds dimensions (L × W × H)</label>
          {form.holdDimensions?.map((hd, idx) => (
            <div key={hd.holdNumber} className="my-1 flex w-[70%] items-center space-x-2">
              <label className="w-20 text-xs font-bold">Hold #{hd.holdNumber}</label>
              <div>
                <InputCell
                  disabled={readonly}
                  name="dimensionl"
                  value={hd.dimensionl ?? ""}
                  onChange={(e) => handleHoldDimensionChange(idx, "dimensionl", e.target.value)}
                  trailingText="L"
                  inputType="number"
                />
              </div>
              <div>
                <InputCell
                  disabled={readonly}
                  name="dimensionw"
                  value={hd.dimensionw ?? ""}
                  onChange={(e) => handleHoldDimensionChange(idx, "dimensionw", e.target.value)}
                  trailingText="W"
                  inputType="number"
                />
              </div>
              <div>
                <InputCell
                  disabled={readonly}
                  name="dimensionh"
                  value={hd.dimensionh ?? ""}
                  onChange={(e) => handleHoldDimensionChange(idx, "dimensionh", e.target.value)}
                  trailingText="H"
                  inputType="number"
                />
              </div>
              <div className="w-3/5">
                <InputCell
                  disabled={readonly}
                  name="comments"
                  value={hd.comments ?? ""}
                  onChange={(e) => handleHoldDimensionChange(idx, "comments", e.target.value)}
                  trailingText="Comments"
                  inputType="text"
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Holds clear/free of obstructions? */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            Are vessel's holds clear and free of any beams, centreline bulkheads or obstructions:
          </label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsClearAndFree"
                value="yes"
                checked={form.holdsClearAndFree === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsClearAndFree"
                value="no"
                checked={form.holdsClearAndFree === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("holdsClearAndFreeComments")}
            >
              Comments
            </button>
          </label>
          {(form.holdsClearAndFreeComments || isCommentsVisible("holdsClearAndFreeComments")) && (
            <textarea
              disabled={readonly}
              name="holdsClearAndFreeComments"
              value={form.holdsClearAndFreeComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Grain & Bale capacities */}
        <div className="col-span-2">
          <label className="block text-sm font-bold">Grain & Bale capacities</label>
          <p className="text-xs font-medium text-gray-500">
            cubic capacity excluding wing/top side tanks but including hatchways:
          </p>
          <div className="mt-6 flex flex-row">
            <label className="w-[70px]">&nbsp;</label>
            <label className="block max-w-[320px] flex-1 text-sm font-bold">Grain</label>
            <label className="block max-w-[320px] flex-1 text-sm font-bold">Bale</label>
          </div>
          {form.holdCapacities?.map((hc, idx) => (
            <div key={hc.holdNumber} className="my-1 flex items-center space-x-2">
              <label className="w-16 text-xs font-bold">Hold #{hc.holdNumber}</label>
              <InputCell
                disabled={readonly}
                name="grainCapacity"
                value={hc.grainCapacity}
                onChange={(e) => handleHoldCapacityChange(idx, "grainCapacity", e.target.value)}
                trailingText="Cu. Metres"
                inputType="number"
              />
              <InputCell
                disabled={readonly}
                name="baleCapacity"
                value={hc.baleCapacity}
                onChange={(e) => handleHoldCapacityChange(idx, "baleCapacity", e.target.value)}
                trailingText="Cu. Metres"
                inputType="number"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Heavy cargoes */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Is vessel strengthened for the carriage of heavy cargoes?:</label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="vesselStrenghtnedHeavyCargoes"
                value="yes"
                checked={form.vesselStrenghtnedHeavyCargoes === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="vesselStrenghtnedHeavyCargoes"
                value="no"
                checked={form.vesselStrenghtnedHeavyCargoes === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="vesselStrenghtnedHeavyCargoes"
                value="n/a"
                checked={form.vesselStrenghtnedHeavyCargoes === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("vesselStrenghtnedHeavyCargoesComments")}
            >
              Comments
            </button>
          </label>
          {(form.vesselStrenghtnedHeavyCargoesComments ||
            isCommentsVisible("vesselStrenghtnedHeavyCargoesComments")) && (
            <textarea
              disabled={readonly}
              name="vesselStrenghtnedHeavyCargoesComments"
              value={form.vesselStrenghtnedHeavyCargoesComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Holds to be empty */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            If vessel is strengthened for the carriage of heavy cargoes, state which holds may be left empty:
          </label>
          <textarea
            disabled={readonly}
            name="vesselStrenghtnedHeavyCargoesHoldsToBeEmpty"
            value={form.vesselStrenghtnedHeavyCargoesHoldsToBeEmpty}
            onChange={handleChange}
            rows={3}
            className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
          />
        </div>
      </div>

      {/* Tanktop steel and suitable for grab discharge */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Is tanktop steel and suitable for grab discharge</label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="tanktopSteelSuitableForGrabDischarge"
                value="yes"
                checked={form.tanktopSteelSuitableForGrabDischarge === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="tanktopSteelSuitableForGrabDischarge"
                value="no"
                checked={form.tanktopSteelSuitableForGrabDischarge === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="tanktopSteelSuitableForGrabDischarge"
                value="n/a"
                checked={form.tanktopSteelSuitableForGrabDischarge === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("tanktopSteelSuitableForGrabDischargeComments")}
            >
              Comments
            </button>
          </label>
          {(form.tanktopSteelSuitableForGrabDischargeComments ||
            isCommentsVisible("tanktopSteelSuitableForGrabDischargeComments")) && (
            <textarea
              disabled={readonly}
              name="tanktopSteelSuitableForGrabDischargeComments"
              value={form.tanktopSteelSuitableForGrabDischargeComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Bulkhead corrugations are vertical or horizontal */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            State whether bulkhead corrugations are vertical or horizontal:
          </label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="corrugationsOrientation"
                value="vertical"
                checked={form.corrugationsOrientation === "vertical"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Vertical</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="corrugationsOrientation"
                value="horizontal"
                checked={form.corrugationsOrientation === "horizontal"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Horizontal</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("corrugationsOrientationComments")}
            >
              Comments
            </button>
          </label>
          {(form.corrugationsOrientationComments || isCommentsVisible("corrugationsOrientationComments")) && (
            <textarea
              disabled={readonly}
              name="corrugationsOrientationComments"
              value={form.corrugationsOrientationComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Tanktop strength (uniform load) */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Tanktop strength (uniform load)</label>
          {form.tanktopsStrengths?.map((tt, i) => (
            <div key={tt.holdNumber} className="my-1 flex items-center space-x-2">
              <label className="w-20 text-xs font-bold">Hold #{tt.holdNumber}</label>
              <InputCell
                disabled={readonly}
                name={"strength" + i}
                value={tt.strength}
                onChange={(e) => handleTanktopsStrengthsChange(i, e.target.value)}
                trailingText="MT/sqm"
                inputType="number"
              />
            </div>
          ))}
        </div>
      </div>

      {/* Are holds CO2 fitted? */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Are holds CO2 fitted?</label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="co2Fitted"
                value="yes"
                checked={form.co2Fitted === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="co2Fitted"
                value="no"
                checked={form.co2Fitted === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="co2Fitted"
                value="n/a"
                checked={form.co2Fitted === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button className={theme.balticQuestionnaire.blueText} onClick={() => toggleComments("co2FittedComments")}>
              Comments
            </button>
          </label>
          {(form.co2FittedComments || isCommentsVisible("co2FittedComments")) && (
            <textarea
              disabled={readonly}
              name="co2FittedComments"
              value={form.co2FittedComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Are holds fitted with smoke detection system? */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Are holds fitted with smoke detection system?</label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="smokeDetection"
                value="yes"
                checked={form.smokeDetection === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="smokeDetection"
                value="no"
                checked={form.smokeDetection === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="smokeDetection"
                value="n/a"
                checked={form.smokeDetection === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("smokeDetectionComments")}
            >
              Comments
            </button>
          </label>
          {(form.smokeDetectionComments || isCommentsVisible("smokeDetectionComments")) && (
            <textarea
              disabled={readonly}
              name="smokeDetectionComments"
              value={form.smokeDetectionComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Is vessel fitted with Australian type approved holds ladders (AHL)?: */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            Is vessel fitted with Australian type approved holds ladders (AHL)?:
          </label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="ahlFitted"
                value="yes"
                checked={form.ahlFitted === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="ahlFitted"
                value="no"
                checked={form.ahlFitted === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="ahlFitted"
                value="n/a"
                checked={form.ahlFitted === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button className={theme.balticQuestionnaire.blueText} onClick={() => toggleComments("ahlFittedComments")}>
              Comments
            </button>
          </label>
          {(form.ahlFittedComments || isCommentsVisible("ahlFittedComments")) && (
            <textarea
              disabled={readonly}
              name="ahlFittedComments"
              value={form.ahlFittedComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Hull stress during cargo handling */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            Is vessel equipped with a functioning class certified Loadmaster or other equipment to ascertain hull stress
            during cargo handling?:
          </label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="hullStressDuringCargoHandling"
                value="yes"
                checked={form.hullStressDuringCargoHandling === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="hullStressDuringCargoHandling"
                value="no"
                checked={form.hullStressDuringCargoHandling === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("hullStressDuringCargoHandlingComments")}
            >
              Comments
            </button>
          </label>
          {(form.hullStressDuringCargoHandlingComments ||
            isCommentsVisible("hullStressDuringCargoHandlingComments")) && (
            <textarea
              disabled={readonly}
              name="hullStressDuringCargoHandlingComments"
              value={form.hullStressDuringCargoHandlingComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Are holds hoppered at hold side? */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Are holds hoppered at hold side?</label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsHopperedAtHoldSide"
                value="yes"
                checked={form.holdsHopperedAtHoldSide === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsHopperedAtHoldSide"
                value="no"
                checked={form.holdsHopperedAtHoldSide === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>

            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsHopperedAtHoldSide"
                value="n/a"
                checked={form.holdsHopperedAtHoldSide === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("holdsHopperedAtHoldSideComments")}
            >
              Comments
            </button>
          </label>
          {(form.holdsHopperedAtHoldSideComments || isCommentsVisible("holdsHopperedAtHoldSideComments")) && (
            <textarea
              disabled={readonly}
              name="holdsHopperedAtHoldSideComments"
              value={form.holdsHopperedAtHoldSideComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Are holds hoppered at forward bulkhead? */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Are holds hoppered at forward bulkhead?</label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsHopperedAtForwardBulkhead"
                value="yes"
                checked={form.holdsHopperedAtForwardBulkhead === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsHopperedAtForwardBulkhead"
                value="no"
                checked={form.holdsHopperedAtForwardBulkhead === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsHopperedAtForwardBulkhead"
                value="n/a"
                checked={form.holdsHopperedAtForwardBulkhead === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("holdsHopperedAtForwardBulkheadComments")}
            >
              Comments
            </button>
          </label>
          {(form.holdsHopperedAtForwardBulkheadComments ||
            isCommentsVisible("holdsHopperedAtForwardBulkheadComments")) && (
            <textarea
              disabled={readonly}
              name="holdsHopperedAtForwardBulkheadComments"
              value={form.holdsHopperedAtForwardBulkheadComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>

        {/* Are holds hoppered at aft bulkhead? */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Are holds hoppered at aft bulkhead?</label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsHopperedAtAftBulkhead"
                value="yes"
                checked={form.holdsHopperedAtAftBulkhead === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsHopperedAtAftBulkhead"
                value="no"
                checked={form.holdsHopperedAtAftBulkhead === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="holdsHopperedAtAftBulkhead"
                value="n/a"
                checked={form.holdsHopperedAtAftBulkhead === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("holdsHopperedAtAftBulkheadComments")}
            >
              Comments
            </button>
          </label>
          {(form.holdsHopperedAtAftBulkheadComments || isCommentsVisible("holdsHopperedAtAftBulkheadComments")) && (
            <textarea
              disabled={readonly}
              name="holdsHopperedAtAftBulkheadComments"
              value={form.holdsHopperedAtAftBulkheadComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Can vessels holds be described as box shaped? */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Can vessels holds be described as box shaped?</label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="boxShaped"
                value="yes"
                checked={form.boxShaped === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="boxShaped"
                value="no"
                checked={form.boxShaped === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="boxShaped"
                value="n/a"
                checked={form.boxShaped === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button className={theme.balticQuestionnaire.blueText} onClick={() => toggleComments("boxShapedComments")}>
              Comments
            </button>
          </label>
          {(form.boxShapedComments || isCommentsVisible("boxShapedComments")) && (
            <textarea
              disabled={readonly}
              name="boxShapedComments"
              value={form.boxShapedComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Measurement of any tank slopes/hoppering  */}
      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-1 mt-4">
          <label className="block text-sm font-bold">Measurement of any tank slopes/hoppering</label>
          <p className="text-xs font-medium text-gray-500">(height and distance from vessel's side at tank top)</p>
          <textarea
            disabled={readonly}
            name="measurementTankSlopesHoppering"
            value={form.measurementTankSlopesHoppering}
            onChange={handleChange}
            rows={3}
            className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
          />
        </div>
      </div>

      {/* Flat floor measurements each hold */}
      <div className="grid grid-cols-4 gap-4">
        <div className="col-span-1 mt-4">
          <label className="block text-sm font-bold">Flat floor measurement of cargo holds at tank top:</label>
          {form.floorMeasurements?.map((fm, idx) => (
            <div key={fm.holdNumber} className="my-1 flex items-center space-x-2">
              <label className="w-20 text-xs font-bold">Hold #{fm.holdNumber}</label>
              <input
                disabled={readonly}
                type="text"
                placeholder="Measurement"
                value={fm.floorMeasurement}
                onChange={(e) => handleFloorMeasurementChange(idx, e.target.value)}
                className={twMerge("w-full rounded-md border p-2 focus:outline-none focus:ring")}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Are vessel's holds electrically ventilated? */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Are vessel's holds electrically ventilated?</label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="electricallyVentilated"
                value="yes"
                checked={form.electricallyVentilated === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="electricallyVentilated"
                value="no"
                checked={form.electricallyVentilated === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="electricallyVentilated"
                value="n/a"
                checked={form.electricallyVentilated === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("electricallyVentilatedComments")}
            >
              Comments
            </button>
          </label>
          {(form.electricallyVentilatedComments || isCommentsVisible("electricallyVentilatedComments")) && (
            <textarea
              disabled={readonly}
              name="electricallyVentilatedComments"
              value={form.electricallyVentilatedComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* Air changes/hour (if ventilated) */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            {" "}
            If vessel's holds are electrically ventilated, state number of airchanges per hour basis empty holds
          </label>
          <input
            disabled={readonly}
            type="text"
            name="airChangesPerHour"
            value={form.airChangesPerHour}
            onChange={handleChange}
            className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
          />
        </div>

        {/* Type of hold paint: */}
        <div className="col-span-1">
          <label className="block text-sm font-bold">Type of hold paint:</label>
          <input
            disabled={readonly}
            type="text"
            name="holdPaintType"
            value={form.holdPaintType}
            onChange={handleChange}
            className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
          />
        </div>
      </div>

      {/* Fitted For Grain Carriage */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">
            Is vessel fitted for carriage of grain in accordance with chapter V1 of SOLAS 1974 and amendments without
            requiring bagging, strapping and securing when loading a full cargo (deadweight) of heavy grain in bulk with
            ends untrimmed?:
          </label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="fittedForGrainCarriage"
                value="yes"
                checked={form.fittedForGrainCarriage === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="fittedForGrainCarriage"
                value="no"
                checked={form.fittedForGrainCarriage === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="fittedForGrainCarriage"
                value="n/a"
                checked={form.fittedForGrainCarriage === "n/a"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">N/A</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("fittedForGrainCarriageComments")}
            >
              Comments
            </button>
          </label>
          {(form.fittedForGrainCarriageComments || isCommentsVisible("fittedForGrainCarriageComments")) && (
            <textarea
              disabled={readonly}
              name="fittedForGrainCarriageComments"
              value={form.fittedForGrainCarriageComments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* A60 Steel Bulkhead */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <label className="block text-sm font-bold">Is the vessel fitted with A60 Steel Bulkhead?:</label>
          <div className="mt-1 flex space-x-4">
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="fittedWithA60"
                value="yes"
                checked={form.fittedWithA60 === "yes"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">Yes</span>
            </label>
            <label className="inline-flex items-center">
              <input
                disabled={readonly}
                type="radio"
                name="fittedWithA60"
                value="no"
                checked={form.fittedWithA60 === "no"}
                onChange={handleChange}
                className="form-radio"
              />
              <span className="ml-2">No</span>
            </label>
          </div>
          <label className="block text-sm font-bold">
            <button
              className={theme.balticQuestionnaire.blueText}
              onClick={() => toggleComments("fittedWithA60Comments")}
            >
              Comments
            </button>
          </label>
          {(form.fittedWithA60Comments || isCommentsVisible("fittedWithA60Comments")) && (
            <textarea
              disabled={readonly}
              name="fittedWithA60Comments"
              value={form.fittedWithA60Comments}
              onChange={handleChange}
              className="mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring"
            />
          )}
        </div>
      </div>

      {/* A60 Location */}
      <div className="mt-4 grid grid-cols-4 gap-4">
        <div className="col-span-1">
          <div className="col-span-1">
            <label className="block text-sm font-bold">
              If vessel is fitted with A60 Steel Bulkhead, provide location:
            </label>
            <input
              type="text"
              name="fittedWithA60Location"
              value={form.fittedWithA60Location}
              onChange={handleChange}
              className={twMerge("mt-1 w-full rounded-md border p-2 focus:outline-none focus:ring")}
            />
          </div>
        </div>
      </div>
    </div>
  );
}
